import React, { Fragment, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";

const Checkout = () => {
    const [form, setForm] = useState({
        current_password: "",
        new_password: "",
        new_confirm_password: "",
        error_list: {}
    });

    const [loading, setLoading] = useState(false);

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleInput = (e) => {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
            error_list: {
                ...prev.error_list,
                [name]: ""
            }
        }));
    };

    // ============================================
    // PASSWORD CHECK
    // ============================================

    const passwordChecks = useMemo(() => {
        const password = form.new_password;

        return {
            length: password.length >= 12,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            symbol: /[^A-Za-z0-9]/.test(password),
            noSpace: password.length > 0 && !/\s/.test(password),
            different:
                password.length > 0 &&
                password !== form.current_password
        };
    }, [form.new_password, form.current_password]);

    const totalChecks = Object.keys(passwordChecks).length;

    const passedChecks =
        Object.values(passwordChecks).filter(Boolean).length;

    const strengthPercent =
        Math.round((passedChecks / totalChecks) * 100);

    const passwordMatch =
        form.new_confirm_password.length > 0 &&
        form.new_password === form.new_confirm_password;

    const getStrengthText = () => {
        if (!form.new_password) {
            return "Enter a new password";
        }

        if (strengthPercent < 40) {
            return "Weak";
        }

        if (strengthPercent < 70) {
            return "Medium";
        }

        if (strengthPercent < 100) {
            return "Strong";
        }

        return "Very Strong";
    };

    const getStrengthClass = () => {
        if (strengthPercent < 40) {
            return "bg-danger";
        }

        if (strengthPercent < 70) {
            return "bg-warning";
        }

        if (strengthPercent < 100) {
            return "bg-info";
        }

        return "bg-success";
    };

    // ============================================
    // SECURE PASSWORD GENERATOR
    // ============================================

    const secureRandomInt = (max) => {
        const array = new Uint32Array(1);

        window.crypto.getRandomValues(array);

        return array[0] % max;
    };

    const generateStrongPassword = () => {
        const uppercase = "ABCDEFGHJKLMNPQRSTUVWXYZ";
        const lowercase = "abcdefghijkmnopqrstuvwxyz";
        const numbers = "23456789";
        const symbols = "!@#$%^&*()-_=+?";

        const all =
            uppercase +
            lowercase +
            numbers +
            symbols;

        let password = "";

        // guarantee each type
        password += uppercase[secureRandomInt(uppercase.length)];
        password += lowercase[secureRandomInt(lowercase.length)];
        password += numbers[secureRandomInt(numbers.length)];
        password += symbols[secureRandomInt(symbols.length)];

        while (password.length < 18) {
            password += all[secureRandomInt(all.length)];
        }

        const chars = password.split("");

        for (let i = chars.length - 1; i > 0; i--) {
            const j = secureRandomInt(i + 1);

            [chars[i], chars[j]] = [chars[j], chars[i]];
        }

        password = chars.join("");

        setForm((prev) => ({
            ...prev,
            new_password: password,
            new_confirm_password: password,
            error_list: {}
        }));

        setShowNew(true);
        setShowConfirm(true);
    };

    // ============================================
    // COPY PASSWORD
    // ============================================

    const copyPassword = async () => {
        if (!form.new_password) {
            swal(
                "Warning",
                "Generate or enter a password first.",
                "warning"
            );

            return;
        }

        try {
            await navigator.clipboard.writeText(
                form.new_password
            );

            swal(
                "Copied",
                "Password copied successfully.",
                "success"
            );
        } catch (error) {
            swal(
                "Error",
                "Unable to copy password.",
                "error"
            );
        }
    };

    // ============================================
    // SUBMIT
    // ============================================

    const passwordSubmit = async (e) => {
        e.preventDefault();

        if (loading) {
            return;
        }

        if (strengthPercent !== 100) {
            swal(
                "Weak Password",
                "Please meet all password security requirements.",
                "warning"
            );

            return;
        }

        if (!passwordMatch) {
            swal(
                "Password Not Match",
                "Confirm password does not match.",
                "warning"
            );

            return;
        }

        const authToken =
            localStorage.getItem("auth_token");

        if (!authToken) {
            swal(
                "Authorization Error",
                "Please login again.",
                "error"
            );

            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                "/apps-password-change",
                {
                    current_password:
                        form.current_password,

                    new_password:
                        form.new_password,

                    new_confirm_password:
                        form.new_confirm_password
                },
                {
                    headers: {
                        Authorization:
                            `Bearer ${authToken}`,
                        Accept: "application/json",
                        "Content-Type":
                            "application/json"
                    }
                }
            );

            if (
                response.data.status ===
                "success"
            ) {
                swal(
                    "Success",
                    response.data.message,
                    "success"
                );

                setForm({
                    current_password: "",
                    new_password: "",
                    new_confirm_password: "",
                    error_list: {}
                });

                setShowCurrent(false);
                setShowNew(false);
                setShowConfirm(false);
            } else {
                swal(
                    "Warning",
                    response.data.message ||
                        "Password could not be changed.",
                    "warning"
                );
            }
        } catch (error) {
            if (
                error.response &&
                error.response.status === 422
            ) {
                setForm((prev) => ({
                    ...prev,
                    error_list:
                        error.response.data.errors ||
                        {}
                }));
            } else if (
                error.response &&
                error.response.status === 429
            ) {
                swal(
                    "Too Many Attempts",
                    "Please wait before trying again.",
                    "warning"
                );
            } else if (
                error.response &&
                error.response.status === 401
            ) {
                swal(
                    "Session Expired",
                    "Please login again.",
                    "error"
                );
            } else {
                swal(
                    "Error",
                    "Something went wrong. Please try again.",
                    "error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const Requirement = ({ valid, children }) => (
        <div
            className={`password-rule ${
                valid
                    ? "password-rule-success"
                    : ""
            }`}
        >
            <span className="rule-icon">
                {valid ? "✓" : "•"}
            </span>

            <span>{children}</span>
        </div>
    );

    return (
        <Fragment>
            <style>
                {`
                .password-page {
                    min-height: 100vh;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 30px 15px;
                    background:
                        radial-gradient(
                            circle at top left,
                            rgba(13,110,253,0.14),
                            transparent 35%
                        ),
                        radial-gradient(
                            circle at bottom right,
                            rgba(111,66,193,0.12),
                            transparent 35%
                        ),
                        #f6f8fc;
                }

                .password-card {
                    width: 100%;
                    max-width: 560px;
                    border: 0;
                    border-radius: 24px;
                    background: #ffffff;
                    box-shadow:
                        0 20px 60px rgba(0,0,0,0.08);
                    overflow: hidden;
                }

                .password-card-header {
                    padding: 32px 30px 18px;
                    text-align: center;
                }

                .security-icon {
                    width: 68px;
                    height: 68px;
                    border-radius: 20px;
                    margin: 0 auto 16px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    background: rgba(13,110,253,0.10);
                    color: #0d6efd;
                    font-size: 32px;
                }

                .password-title {
                    margin: 0;
                    font-size: 28px;
                    font-weight: 700;
                    color: #1e293b;
                }

                .password-subtitle {
                    margin-top: 8px;
                    margin-bottom: 0;
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.6;
                }

                .password-card-body {
                    padding: 15px 30px 32px;
                }

                .password-input-group {
                    position: relative;
                    margin-bottom: 18px;
                }

                .password-input {
                    width: 100%;
                    height: 55px;
                    border: 1px solid #dbe2ea;
                    border-radius: 14px;
                    padding: 0 54px 0 17px;
                    font-size: 15px;
                    outline: none;
                    transition: all .2s ease;
                    background: #fff;
                }

                .password-input:focus {
                    border-color: #0d6efd;
                    box-shadow:
                        0 0 0 4px rgba(13,110,253,0.10);
                }

                .password-toggle {
                    position: absolute;
                    right: 9px;
                    top: 9px;
                    height: 37px;
                    min-width: 45px;
                    border: 0;
                    border-radius: 10px;
                    background: #f1f5f9;
                    color: #475569;
                    font-size: 13px;
                    cursor: pointer;
                }

                .password-toggle:hover {
                    background: #e2e8f0;
                }

                .error-text {
                    display: block;
                    margin-top: 6px;
                    padding-left: 3px;
                    color: #dc3545;
                    font-size: 13px;
                }

                .password-tools {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    margin-bottom: 18px;
                }

                .tool-btn {
                    min-height: 46px;
                    border-radius: 12px;
                    font-weight: 600;
                    border: 1px solid #d7e0ea;
                    background: #ffffff;
                    cursor: pointer;
                    transition: .2s;
                    padding: 10px 12px;
                }

                .tool-btn:hover {
                    border-color: #0d6efd;
                    color: #0d6efd;
                    background: #f8fbff;
                }

                .strength-box {
                    margin-top: 8px;
                    margin-bottom: 22px;
                    padding: 18px;
                    border-radius: 16px;
                    background: #f8fafc;
                    border: 1px solid #edf1f5;
                }

                .strength-heading {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 10px;
                }

                .strength-heading span:first-child {
                    font-size: 14px;
                    font-weight: 600;
                    color: #334155;
                }

                .strength-status {
                    font-size: 13px;
                    font-weight: 700;
                }

                .strength-progress {
                    height: 8px;
                    border-radius: 10px;
                    overflow: hidden;
                    background: #e7ecf2;
                    margin-bottom: 14px;
                }

                .strength-progress-bar {
                    height: 100%;
                    transition: width .25s ease;
                }

                .password-rule {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 7px;
                    color: #94a3b8;
                    font-size: 13px;
                }

                .password-rule-success {
                    color: #198754;
                }

                .rule-icon {
                    width: 16px;
                    font-weight: bold;
                }

                .match-message {
                    margin-top: 8px;
                    font-size: 13px;
                    font-weight: 600;
                }

                .match-success {
                    color: #198754;
                }

                .match-error {
                    color: #dc3545;
                }

                .submit-btn {
                    width: 100%;
                    height: 54px;
                    border: 0;
                    border-radius: 14px;
                    background: #0d6efd;
                    color: white;
                    font-size: 16px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: .2s ease;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow:
                        0 10px 25px rgba(13,110,253,0.20);
                }

                .submit-btn:disabled {
                    opacity: .6;
                    cursor: not-allowed;
                }

                .security-note {
                    margin-top: 15px;
                    text-align: center;
                    color: #94a3b8;
                    font-size: 12px;
                    line-height: 1.5;
                }

                @media (max-width: 576px) {

                    .password-page {
                        padding: 18px 12px;
                        align-items: flex-start;
                    }

                    .password-card {
                        margin-top: 12px;
                        border-radius: 19px;
                    }

                    .password-card-header {
                        padding:
                            25px 20px 14px;
                    }

                    .password-card-body {
                        padding:
                            12px 20px 25px;
                    }

                    .password-title {
                        font-size: 23px;
                    }

                    .security-icon {
                        width: 58px;
                        height: 58px;
                        font-size: 27px;
                        border-radius: 17px;
                    }

                    .password-input {
                        height: 52px;
                        font-size: 14px;
                    }

                    .password-tools {
                        grid-template-columns: 1fr;
                    }

                    .strength-box {
                        padding: 15px;
                    }
                }
                `}
            </style>

            <div className="password-page">

                <div className="password-card">

                    <div className="password-card-header">

                        <div className="security-icon">
                            🔐
                        </div>

                        <h2 className="password-title">
                            Change Password
                        </h2>
                        <p
                          className="password-subtitle"
                          style={{
                            color: "#dc3545",
                            fontWeight: "600",
                            fontSize: "14px"
                          }}
                        >
                          ⚠️ For your security, please update your password before continuing.
                        </p>

                    </div>

                    <div className="password-card-body">

                        <form onSubmit={passwordSubmit}>

                            {/* CURRENT PASSWORD */}

                            <div className="password-input-group">

                                <input
                                    type={
                                        showCurrent
                                            ? "text"
                                            : "password"
                                    }
                                    name="current_password"
                                    className="password-input"
                                    placeholder="Enter Current Password"
                                    value={form.current_password}
                                    onChange={handleInput}
                                    autoComplete="current-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() =>
                                        setShowCurrent(
                                            !showCurrent
                                        )
                                    }
                                >
                                    {showCurrent
                                        ? "Hide"
                                        : "Show"}
                                </button>

                                {form.error_list
                                    .current_password && (
                                    <small className="error-text">
                                        {
                                            form
                                                .error_list
                                                .current_password[0]
                                        }
                                    </small>
                                )}

                            </div>


                            {/* NEW PASSWORD */}

                            <div className="password-input-group">

                                <input
                                    type={
                                        showNew
                                            ? "text"
                                            : "password"
                                    }
                                    name="new_password"
                                    className="password-input"
                                    placeholder="Enter New Strong Password"
                                    value={form.new_password}
                                    onChange={handleInput}
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() =>
                                        setShowNew(!showNew)
                                    }
                                >
                                    {showNew
                                        ? "Hide"
                                        : "Show"}
                                </button>

                                {form.error_list
                                    .new_password && (
                                    <small className="error-text">
                                        {
                                            form
                                                .error_list
                                                .new_password[0]
                                        }
                                    </small>
                                )}

                            </div>


                            {/* PASSWORD TOOLS */}

                            <div className="password-tools">

                                <button
                                    type="button"
                                    className="tool-btn"
                                    onClick={
                                        generateStrongPassword
                                    }
                                >
                                    ⚡ Generate Strong Password
                                </button>

                                <button
                                    type="button"
                                    className="tool-btn"
                                    onClick={copyPassword}
                                >
                                    📋 Copy Password
                                </button>

                            </div>


                            {/* CONFIRM PASSWORD */}

                            <div className="password-input-group">

                                <input
                                    type={
                                        showConfirm
                                            ? "text"
                                            : "password"
                                    }
                                    name="new_confirm_password"
                                    className="password-input"
                                    placeholder="Confirm New Password"
                                    value={
                                        form.new_confirm_password
                                    }
                                    onChange={handleInput}
                                    autoComplete="new-password"
                                    required
                                />

                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() =>
                                        setShowConfirm(
                                            !showConfirm
                                        )
                                    }
                                >
                                    {showConfirm
                                        ? "Hide"
                                        : "Show"}
                                </button>

                                {form.error_list
                                    .new_confirm_password && (
                                    <small className="error-text">
                                        {
                                            form
                                                .error_list
                                                .new_confirm_password[0]
                                        }
                                    </small>
                                )}

                                {form
                                    .new_confirm_password && (
                                    <div
                                        className={`match-message ${
                                            passwordMatch
                                                ? "match-success"
                                                : "match-error"
                                        }`}
                                    >
                                        {passwordMatch
                                            ? "✓ Passwords match"
                                            : "✕ Passwords do not match"}
                                    </div>
                                )}

                            </div>


                            {/* PASSWORD STRENGTH - BELOW CONFIRM */}

                            <div className="strength-box">

                                <div className="strength-heading">

                                    <span>
                                        Password Strength
                                    </span>

                                    <span
                                        className="strength-status"
                                    >
                                        {getStrengthText()}
                                    </span>

                                </div>

                                <div className="strength-progress">

                                    <div
                                        className={`strength-progress-bar ${getStrengthClass()}`}
                                        style={{
                                            width:
                                                `${
                                                    form.new_password
                                                        ? strengthPercent
                                                        : 0
                                                }%`
                                        }}
                                    />

                                </div>

                                <Requirement
                                    valid={
                                        passwordChecks.length
                                    }
                                >
                                    Minimum 12 characters
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.uppercase
                                    }
                                >
                                    At least one uppercase
                                    letter
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.lowercase
                                    }
                                >
                                    At least one lowercase
                                    letter
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.number
                                    }
                                >
                                    At least one number
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.symbol
                                    }
                                >
                                    At least one special
                                    character
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.noSpace
                                    }
                                >
                                    No spaces
                                </Requirement>

                                <Requirement
                                    valid={
                                        passwordChecks.different
                                    }
                                >
                                    Different from current
                                    password
                                </Requirement>

                            </div>


                            {/* SUBMIT */}

                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={
                                    loading ||
                                    strengthPercent !== 100 ||
                                    !passwordMatch ||
                                    !form.current_password
                                }
                            >
                                {loading
                                    ? "Updating Password..."
                                    : "Update Password"}
                            </button>


                            <div className="security-note">
                                Never share your password with
                                anyone. Use a unique password
                                that you do not use on other
                                websites.
                            </div>

                        </form>

                    </div>
                </div>

            </div>

        </Fragment>
    );
};

export default Checkout;